// O campo "Para" e "Cc", com sugestões da agenda.
//
// A agenda existe para ser usada aqui: capturar contatos e nunca oferecê-los
// de volta seria guardar por guardar.
//
// O campo continua sendo TEXTO LIVRE. Escrever para quem não está na agenda é
// o caso mais comum que existe, e transformar isto num seletor obrigaria a
// cadastrar antes de mandar a primeira mensagem — que é exatamente ao
// contrário de como um e-mail funciona.

import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import * as contatos from "../api/contatos";
import { limparEndereco, separarEnderecos } from "./endereco";

export function CampoDeEnderecos({
  id,
  valor,
  aoMudar,
  autoFocus,
  className,
}: {
  id: string;
  valor: string;
  aoMudar: (v: string) => void;
  autoFocus?: boolean;
  className?: string;
}) {
  const [aberto, setAberto] = useState(false);
  const [destacado, setDestacado] = useState(0);
  const caixa = useRef<HTMLDivElement>(null);

  // Só o trecho DEPOIS da última vírgula: a sugestão é para o endereço que
  // está sendo digitado agora, não para a linha inteira.
  const emDigitacao = limparEndereco(valor.split(/[,;]/).pop() ?? "");

  const busca = useQuery({
    queryKey: ["contatos", emDigitacao],
    queryFn: ({ signal }) => contatos.listar({ q: emDigitacao }, signal),
    // Duas letras antes de sugerir: com uma, a lista é a agenda inteira e não
    // ajuda a escolher.
    enabled: aberto && emDigitacao.length >= 2,
    staleTime: 60_000,
  });

  // Limpo antes de comparar: um endereço com invisível colado não casaria
  // com ele mesmo, e a agenda sugeriria de novo quem já está no campo.
  const jaNoCampo = new Set(separarEnderecos(valor).map((e) => e.toLowerCase()));
  const sugestoes = (busca.data?.itens ?? [])
    .filter((c) => c.email && !jaNoCampo.has(c.email.toLowerCase()))
    .slice(0, 6);

  useEffect(() => setDestacado(0), [emDigitacao]);

  useEffect(() => {
    function fora(e: MouseEvent) {
      if (caixa.current && !caixa.current.contains(e.target as Node)) setAberto(false);
    }
    document.addEventListener("mousedown", fora);
    return () => document.removeEventListener("mousedown", fora);
  }, []);

  function escolher(c: contatos.Contato) {
    const partes = valor.split(/[,;]/);
    partes[partes.length - 1] = ` ${c.email}`;
    // Vírgula no fim para a pessoa continuar digitando o próximo endereço
    // sem ter que lembrar de pôr o separador.
    aoMudar(partes.join(",").replace(/^\s+/, "") + ", ");
    setAberto(false);
  }

  return (
    <div ref={caixa} className="relative min-w-0 flex-1">
      <input
        id={id}
        value={valor}
        autoFocus={autoFocus}
        autoComplete="off"
        onChange={(e) => {
          aoMudar(e.target.value);
          setAberto(true);
        }}
        onFocus={() => setAberto(true)}
        onKeyDown={(e) => {
          if (!aberto || sugestoes.length === 0) return;
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setDestacado((d) => (d + 1) % sugestoes.length);
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setDestacado((d) => (d - 1 + sugestoes.length) % sugestoes.length);
          } else if (e.key === "Enter" || e.key === "Tab") {
            // Enter escolhe a sugestão em vez de ENVIAR a mensagem. Enviar
            // pela metade do endereço é o erro que este atalho evita.
            e.preventDefault();
            escolher(sugestoes[destacado]!);
          } else if (e.key === "Escape") {
            setAberto(false);
          }
        }}
        className={className}
      />

      {aberto && sugestoes.length > 0 && (
        <ul
          role="listbox"
          className="absolute left-0 right-0 top-full z-50 mt-1 overflow-hidden rounded-lg border border-linha bg-white shadow-lg"
        >
          {sugestoes.map((c, i) => (
            <li key={c.uuid}>
              <button
                type="button"
                onMouseEnter={() => setDestacado(i)}
                onClick={() => escolher(c)}
                className={`flex w-full items-baseline gap-2 px-3 py-2 text-left text-sm ${
                  i === destacado ? "bg-acao-clara" : ""
                }`}
              >
                <span className="truncate font-medium text-tinta">{c.nome || c.email}</span>
                {c.nome && <span className="truncate text-xs text-tinta-fraca">{c.email}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
